import { Dialog } from '@madpaws/design-system';
import { Formik } from 'formik';
import React from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

import { DOM_CONTAINER_ID } from '~/components/constants';
import { AuthApi } from '~/services/auth/authApi';
import { getJWTData } from '~/utils/auth';

import { EmailLoginForm } from './EmailLoginForm';
import { passwordValidationSchema } from './utils';
import { PASSWORD_LOGIN_GRANT_TYPE, AuthMethods, RecaptchaActions } from '../constants';
import { useAuthenticationContext } from '../useAuthenticationContext';

import type { OnLoginSuccessHandler } from '../types';
import type { ReactElement } from 'react';

type Props = {
  email: string;
  onLoginSuccess: OnLoginSuccessHandler;
};

export type EmailLoginFormValues = {
  email: string;
  password: string;
};

export const EmailLoginDialog = ({ onLoginSuccess, email }: Props): ReactElement => {
  const { executeRecaptcha } = useGoogleReCaptcha();

  const [apiError, setApiError] = React.useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const { setEmailLoginDialogVisible, isEmailLoginDialogOpen } = useAuthenticationContext();

  const onRequestClose = (): void => {
    setEmailLoginDialogVisible(false);
  };

  const onSubmitForm = async (values: EmailLoginFormValues): Promise<void> => {
    setApiError(null);
    setIsSubmitting(true);

    const recaptchaTokenV3 = await executeRecaptcha?.(RecaptchaActions.SIGN_IN);

    try {
      const response = await AuthApi.getAuthToken({
        client_id: Number(process.env.NEXT_PUBLIC_CLIENT_ID ?? 0),
        client_secret: process.env.NEXT_PUBLIC_CLIENT_SECRET ?? '',
        code: '',
        grant_type: PASSWORD_LOGIN_GRANT_TYPE,
        password: values.password,
        username: values.email,
        recaptchaTokenV3,
      });

      const jwtData = getJWTData(response.access_token as string);
      const userId = jwtData?.sub as number;

      onLoginSuccess(response, { email: values.email, userId }, AuthMethods.PASSWORD);
      setEmailLoginDialogVisible(false);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      setApiError(error[0]?.detail);
    }

    setIsSubmitting(false);
  };

  const formInitialValues = { password: '', email };

  return (
    <Dialog
      domContainerId={DOM_CONTAINER_ID}
      heading="Welcome to Mad Paws"
      isOpen={isEmailLoginDialogOpen}
      onRequestClose={onRequestClose}
    >
      <Formik
        initialValues={formInitialValues}
        onSubmit={onSubmitForm}
        validationSchema={passwordValidationSchema}
      >
        <EmailLoginForm apiError={apiError} isAPILoading={isSubmitting} />
      </Formik>
    </Dialog>
  );
};
